import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

export interface AuthResponse
{
    accessToken: string;
    expiresAt: string;
    refreshToken?: string;
    user: User;
}

/**
 * Accesso con token JWT dell'API .NET: gli utenti e i ruoli sono quelli già presenti su database.
 * Il token di accesso dura poco e viene rinnovato con il refresh token.
 */
@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated = false;
    private _refreshing$: Observable<boolean> | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    set refreshToken(token: string)
    {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string
    {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /** Accesso con email e password. */
    signIn(credentials: { email: string; password: string }): Observable<User>
    {
        return this._httpClient.post<AuthResponse>('api/auth/login', credentials).pipe(
            tap((response) => this._store(response)),
            map((response) => response.user),
        );
    }

    /** Sostituisce la sessione corrente con quella di un altro utente (accesso come altro utente). */
    startSession(response: AuthResponse): void
    {
        this._store(response);
    }

    /** Rinnova il token di accesso usando il refresh token (una sola richiesta alla volta). */
    refresh(): Observable<boolean>
    {
        if ( !this.refreshToken || !this.accessToken )
        {
            return of(false);
        }

        if ( !this._refreshing$ )
        {
            this._refreshing$ = this._httpClient.post<AuthResponse>('api/auth/refresh', {
                accessToken : this.accessToken,
                refreshToken: this.refreshToken,
            }).pipe(
                tap((response) => this._store(response)),
                map(() => true),
                catchError(() => of(false)),
                tap(() => this._refreshing$ = null),
                shareReplay(1),
            );
        }

        return this._refreshing$;
    }

    /** Chiude la sessione. */
    signOut(): Observable<boolean>
    {
        const done = () =>
        {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            this._authenticated = false;
        };

        if ( this.accessToken && !AuthUtils.isTokenExpired(this.accessToken) )
        {
            return this._httpClient.post('api/auth/logout', {}).pipe(
                catchError(() => of(null)),
                tap(() => done()),
                map(() => true),
            );
        }

        done();
        return of(true);
    }

    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', {email});
    }

    resetPassword(body: { email: string; code: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', body);
    }

    confirmEmail(body: { userId: string; code: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/confirm-email', body);
    }

    /** Verifica lo stato di autenticazione, rinnovando il token se serve. */
    check(): Observable<boolean>
    {
        if ( this._authenticated )
        {
            return of(true);
        }

        if ( !this.accessToken )
        {
            return of(false);
        }

        // token scaduto: si prova a rinnovarlo, poi si caricano i dati dell'utente
        if ( AuthUtils.isTokenExpired(this.accessToken, 30) )
        {
            // (il rinnovo restituisce anche i dati aggiornati dell'utente)
            return this.refresh();
        }

        return this._loadUser();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _store(response: AuthResponse): void
    {
        this.accessToken = response.accessToken;
        if ( response.refreshToken )
        {
            this.refreshToken = response.refreshToken;
        }
        else
        {
            localStorage.removeItem('refreshToken');
        }

        this._authenticated = true;
        this._userService.user = response.user;
    }

    private _loadUser(): Observable<boolean>
    {
        return this._userService.get().pipe(
            tap(() => this._authenticated = true),
            map(() => true),
            catchError(() => of(false)),
        );
    }
}
